/**
 * The family's number as people see it (release 18, owner 2026-10-08). "Nomor KK" is the
 * government (Disdukcapil) KK number, 16 digits, for everything a person sees and does: forms,
 * the family page, lists, search, prints and downloads, Impor, transfers. A family without one
 * shows the parish number SATU generated for it, with the note *No. KK Disdukcapil belum
 * tersedia*; once the government number is entered it is the shown number, and the parish
 * number stays as history (searchable, never re-issued).
 *
 * Pure and shared: the screens, the worker's reads, the prints and Impor read it alike.
 */

import { format } from './strings/index'
import ui from './strings/ui.id.json' with { type: 'json' }
import type { PemakaiKk, PemakaiKode } from './sync/ganda'

/** The note beside a parish number shown for want of a government one. */
export const CATATAN_KK_PAROKI: string = ui.nomorKk.catatan

export interface NomorKkTampil {
  /** The government KK number, else the parish number ('' when the family has neither). */
  nomor: string
  /** The note when `nomor` is the parish number; null for a government number. */
  catatan: string | null
}

const teks = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')

/** The number a family shows: its government KK number, else its parish number with the note. */
export function nomorKkTampil(pemerintah: unknown, paroki: unknown): NomorKkTampil {
  const p = teks(pemerintah)
  if (p !== '') return { nomor: p, catatan: null }
  return { nomor: teks(paroki), catatan: CATATAN_KK_PAROKI }
}

/**
 * The mark a parish number carries where its note would wrap every row (a list on screen, a
 * printed list): the note is then said once, under or above the table, after the same mark.
 */
export const TANDA_KK_PAROKI = '*'

/** A list cell's number: the parish number with its mark, a government one as it is. */
export function nomorKkBertanda(kk: NomorKkTampil): string {
  return (kk.catatan ?? null) === null || kk.nomor === ''
    ? kk.nomor
    : `${kk.nomor}${TANDA_KK_PAROKI}`
}

/** The note a list says once for its marked numbers: "* No. KK Disdukcapil belum tersedia". */
export const KETERANGAN_TANDA_KK = `${TANDA_KK_PAROKI} ${CATATAN_KK_PAROKI}`

/** The shown number on one line: "0231 (No. KK Disdukcapil belum tersedia)", or the number. */
export function nomorKkSebaris(kk: NomorKkTampil): string {
  // A row read without the note (an older reading) is a number alone, never "{catatan}".
  const catatan = kk.catatan ?? null
  if (catatan === null || kk.nomor === '') return kk.nomor
  return format(ui.nomorKk.sebaris, { nomor: kk.nomor, catatan })
}

/**
 * The number a sync card or the activity list names a family without a head by (review M-9):
 * its parish number, never its government one (no card carries it), on one line with the note
 * when the parish number is the family's shown number. Null without a parish number.
 */
export function nomorKkKartu(pemerintah: unknown, paroki: unknown): string | null {
  const p = teks(paroki)
  if (p === '') return null
  return teks(pemerintah) === '' ? nomorKkSebaris({ nomor: p, catatan: CATATAN_KK_PAROKI }) : p
}

/** The end a one-line number carries for its note: " (No. KK Disdukcapil belum tersedia)". */
const AKHIR_SEBARIS = format(ui.nomorKk.sebaris, { nomor: '', catatan: CATATAN_KK_PAROKI })

/**
 * A number on one line as `nomorKkSebaris` writes it (a row's own reading, a downloaded cell),
 * read back: the number, and the note when it carried it. Anything else is a number alone.
 */
export function bacaNomorKkSebaris(kk: string): NomorKkTampil {
  return kk.endsWith(AKHIR_SEBARIS)
    ? { nomor: kk.slice(0, -AKHIR_SEBARIS.length), catatan: CATATAN_KK_PAROKI }
    : { nomor: kk, catatan: null }
}

/**
 * The shown number inside a bracket ("Keluarga Yohanes Yufuwai (KK …)"): the note after a comma,
 * "0231, No. KK Disdukcapil belum tersedia", never a bracket in a bracket. Takes the number as
 * `nomorKkTampil` gives it, or as a row carries it on one line already (`nomorKkSebaris`).
 */
export function nomorKkDalamKurung(kk: NomorKkTampil | string): string {
  const tampil: NomorKkTampil =
    typeof kk !== 'string'
      ? { nomor: kk.nomor, catatan: kk.catatan ?? null }
      : bacaNomorKkSebaris(kk)
  if (tampil.catatan === null || tampil.nomor === '') return tampil.nomor
  return format(ui.nomorKk.dalamKurung, { nomor: tampil.nomor, catatan: tampil.catatan })
}

/** Spaces and dots as people type a KK number ("9101 0101.0101 0031"); nothing else goes. */
const PEMISAH_KK = /[\s.]+/g

/**
 * A government KK number as typed or read from a file: the 16 digits once spaces and dots are
 * dropped (full-width digits read as digits); null for anything else (another length, letters,
 * a dash). The caller words a refusal with *Nomor KK terdiri dari 16 angka.*
 */
export function bacaNomorKkPemerintah(isi: unknown): string | null {
  if (typeof isi !== 'string') return null
  const digit = isi.normalize('NFKC').replace(PEMISAH_KK, '')
  return /^\d{16}$/.test(digit) ? digit : null
}

/** A Kode keluarga as stored: trimmed, inner spaces one; null when empty. */
export function rapikanKodeKeluarga(isi: unknown): string | null {
  if (typeof isi !== 'string') return null
  const t = isi.trim().replace(/\s+/g, ' ')
  return t === '' ? null : t
}

/** A Kode keluarga as two families' codes are compared: "ap-001" is "AP-001". */
export function kunciKodeKeluarga(isi: unknown): string | null {
  return rapikanKodeKeluarga(isi)?.toUpperCase() ?? null
}

// ---------------------------------------------------------------------------
// The warning when another family holds the same number or code (owner, 2026-10-08)
// ---------------------------------------------------------------------------

/** The names a sentence lists: each head once, a family without one said so. */
export function gabungNamaKepala(kepala: readonly (string | null)[]): string | null {
  const nama = [
    ...new Set(
      kepala.map((k) => (k === null || k.trim() === '' ? ui.nomorKk.tanpaKepala : k.trim())),
    ),
  ]
  return nama.length === 0 ? null : nama.join(', ')
}

/** The sentences that are said, in order, as one warning; null when none is. */
function kalimatPeringatan(kalimat: readonly (string | null)[]): string | null {
  const ada = kalimat.filter((k): k is string => k !== null)
  return ada.length === 0 ? null : ada.join(' ')
}

/**
 * The warning under a government KK number another live family also holds (a married child still
 * on the parents' Disdukcapil KK): *Nomor KK ini juga dipakai keluarga {nama kepala}.* for the
 * parish's own, the device's and the server's alike; *Nomor KK ini juga dipakai keluarga lain di
 * paroki ini.* for a family of the parish outside a ketua's lingkungan (review I-1: never named
 * to them); *Nomor KK ini juga dipakai keluarga di paroki lain.* for another parish's, whose
 * names never reach a device. Null when no one else holds it, or nothing could be asked. Never a
 * refusal: the save goes through.
 */
export function peringatanNomorKk(
  perangkat: readonly (string | null)[],
  server: PemakaiKk | null,
): string | null {
  const nama = gabungNamaKepala([...perangkat, ...(server?.paroki_ini ?? [])])
  return kalimatPeringatan([
    nama === null ? null : format(ui.nomorKk.jugaDipakai, { nama }),
    server?.luar_lingkup === true ? ui.nomorKk.jugaDipakaiLuarLingkup : null,
    server?.paroki_lain === true ? ui.nomorKk.jugaDipakaiParokiLain : null,
  ])
}

/**
 * The same for a Kode keluarga, the parish's own: its other holders in the parish, by their heads,
 * and for a ketua those outside their lingkungan by a sentence that names no one.
 */
export function peringatanKode(
  perangkat: readonly (string | null)[],
  server: PemakaiKode | null,
): string | null {
  const nama = gabungNamaKepala([...perangkat, ...(server?.paroki_ini ?? [])])
  return kalimatPeringatan([
    nama === null ? null : format(ui.nomorKk.kodeJugaDipakai, { nama }),
    server?.luar_lingkup === true ? ui.nomorKk.kodeJugaDipakaiLuarLingkup : null,
  ])
}
